import { Add, Clear, Close, Create } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isEmailValid } from "../../../helpers";
import { myClassesServices, myExpariencesService } from "../../../services";
import { initialClassState } from "./classInitialisation";

const PREFIX = "ClassCreator";

const classes = {
  dialogRoot: `${PREFIX}-dialogRoot`,
  classLogo: `${PREFIX}-classLogo`,
  modifyLogoButton: `${PREFIX}-modifyLogoButton`,
  membersContainer: `${PREFIX}-membersContainer`,
  mainScrollableContainer: `${PREFIX}-mainScrollableContainer`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.dialogRoot}`]: {
    maxWidth: "650px",
    minWidth: "350px",
    minHeight: "200px",
    padding: "0.5rem 1rem",
  },

  [`& .${classes.classLogo}`]: {
    maxWidth: "150px",
  },

  [`& .${classes.modifyLogoButton}`]: {
    backgroundColor: "#383838",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      "& > svg": {
        fill: "#383838",
      },
    },
  },

  [`& .${classes.mainScrollableContainer}`]: {
    overflow: "auto",
    maxHeight: "90vh",
    padding: "0.5rem",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },

  [`& .${classes.membersContainer}`]: {
    padding: "0.5rem",
    overflowY: "auto",
    maxHeight: "375px",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },
}));

const ClassCreator = ({
  open,
  onClose,
  selectedClass,
  isAdmin,
  expId,
  addGroupToExparienceAfterCreation,
  isClassManager = false,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [confirmDelete, setConfirmDelete] = useState({
    index: -1,
    member: null,
  });
  const [classState, setClassState] = useState(selectedClass);
  const [creatingOrUpdating, setCreatingOrUpdating] = useState({
    file: false,
    group: false,
  });

  const handleChangeState = useCallback(
    (key, value) => {
      setClassState({ ...classState, [key]: value });
    },
    [classState]
  );

  useEffect(() => {
    if (selectedClass) setClassState(selectedClass);
  }, [selectedClass]);

  const handleMemberChange = (index, key, value) => {
    const tmp = [...classState.members];
    tmp[index] = { ...tmp[index], [key]: value };
    setClassState({ ...classState, members: tmp });
  };

  const handleClose = useCallback(() => {
    setCreatingOrUpdating({
      file: false,
      group: false,
    });
    onClose();
  }, [onClose]);

  const handleAddMember = () => {
    const tmp = [...classState.members];
    tmp.push({ lastname: "", firstname: "", email: "" });
    setClassState({ ...classState, members: tmp });
  };

  const handleDeleteMember = (index) => {
    const tmp = [...classState.members];
    if (classState.members.length === 1) tmp[0] = initialClassState.members[0];
    else tmp.splice(index, 1);
    setClassState({ ...classState, members: tmp });
  };

  const createOrUpdateClass = () => {
    setCreatingOrUpdating({ ...creatingOrUpdating, group: true });
    const routeToCall = !selectedClass?.id
      ? myClassesServices.createGroup
      : myClassesServices.updateGroup;
    const payload = { ...classState, name: classState.name.trim() };
    if (selectedClass?.id) {
      payload.members = classState.members.map((elem) => {
        const { groups, ...rest } = elem;
        return rest;
      });
      delete payload.createdBy;
    }
    if (!payload.imageUrl) delete payload.imageUrl;
    routeToCall(payload, payload.id)
      .then(async (res) => {
        enqueueSnackbar(
          !selectedClass?.id
            ? t("toasts.createGroupSuccess")
            : t("toasts.updateGroupSuccess"),
          {
            variant: "success",
          }
        );
        addGroupToExparienceAfterCreation(
          isClassManager
            ? res
            : {
                groupId: res.id,
                duration: 30,
              },
          !!selectedClass?.id,
          res
        );
        handleClose();
      })
      .catch((err) => {
        console.log("error =>", err);
        enqueueSnackbar(
          !selectedClass?.id
            ? t("toasts.createGroupError")
            : t("toasts.updateGroupError"),
          {
            variant: "error",
          }
        );
        setCreatingOrUpdating({ ...creatingOrUpdating, group: false });
      });
  };

  const isAvailableForCreation = () => {
    const { name, members } = classState;
    if (name.length <= 0 || members.some((m) => !isEmailValid(m.email)))
      return true;
    return false;
  };

  const getImageFromS3 = (file) => {
    setCreatingOrUpdating({ ...creatingOrUpdating, file: true });
    myExpariencesService
      .getClassImageUrlFromS3(file)
      .then((res) => {
        setClassState({
          ...classState,
          imageUrl: res.url,
        });
        setCreatingOrUpdating({ ...creatingOrUpdating, file: false });
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        setCreatingOrUpdating({ ...creatingOrUpdating, file: false });
      });
  };

  if (!classState) return null;
  else
    return (
      <>
        <Dialog open={confirmDelete.index > -1 ? true : false}>
          <Root>
            <DialogTitle id="alert-dialog-confirm-delete-title">
              {t("myExpariences.class.updateClassTitle")}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-confirm-delete-description">
                {t("myExpariences.class.deleteMemberMessage")}
              </DialogContentText>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ display: "flex", margin: "0.5rem 0" }}>
                  <DialogContentText id="alert-dialog-confirm-delete-member-infos">
                    {confirmDelete?.member?.firstname}{" "}
                    {confirmDelete?.member?.lastname}{" "}
                  </DialogContentText>
                </Box>
                <DialogContentText id="alert-dialog-confirm-delete-member-email">
                  {confirmDelete?.member?.email}
                </DialogContentText>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() =>
                  setConfirmDelete({ ...confirmDelete, index: -1 })
                }
              >
                {t("myExpariences.class.cancelDeleteButton")}
              </Button>
              <Button
                onClick={() => {
                  handleDeleteMember(confirmDelete.index);
                  setConfirmDelete({ ...confirmDelete, index: -1 });
                }}
              >
                {t("myExpariences.class.confirmDeleteButton")}
              </Button>
            </DialogActions>
          </Root>
        </Dialog>
        <Dialog open={open} PaperProps={{ sx: { margin: "0 16px" } }}>
          <Root>
            <Box className={classes.dialogRoot}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "1rem",
                }}
              >
                <Typography variant="h6">
                  {selectedClass?.id
                    ? t("myExpariences.class.updateClassTitle")
                    : t("myExpariences.class.createClassTitle")}
                </Typography>
                <IconButton color="primary" onClick={handleClose}>
                  <Close />
                </IconButton>
              </Box>
              <Box className={classes.mainScrollableContainer}>
                <TextField
                  name="name"
                  label={t("myExpariences.class.className")}
                  value={classState.name}
                  onChange={(e) => handleChangeState("name", e.target.value)}
                  fullWidth
                  id="class name"
                  sx={{ marginBottom: "1rem" }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                  }}
                >
                  <Box
                    sx={{
                      marginBottom: "1rem",
                      display: "flex",
                      justifyContent: "center",
                      width: "50%",
                      marginRight: "1rem",
                    }}
                  >
                    {creatingOrUpdating.file ? (
                      <CircularProgress size={50} />
                    ) : (
                      !classState.imageUrl && (
                        <Box>
                          <input
                            type="file"
                            accept="image/*"
                            name="classLogo"
                            id="icon-button-class-logo-add"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              getImageFromS3(e.target.files[0]);
                            }}
                          />
                          <label htmlFor="icon-button-class-logo-add">
                            <Button
                              variant="contained"
                              color="primary"
                              component="span"
                              sx={{ marginTop: "0.5rem" }}
                            >
                              {t("myExpariences.class.selectLogoButton")}
                            </Button>
                          </label>
                        </Box>
                      )
                    )}
                    {classState.imageUrl && (
                      <Box sx={{ position: "relative" }}>
                        <img
                          id="class logo"
                          className={classes.classLogo}
                          alt="class logo"
                          src={classState.imageUrl}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                          }}
                        >
                          <input
                            type="file"
                            accept="image/*"
                            name="classLogoModify"
                            id="icon-button-file"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              getImageFromS3(e.target.files[0]);
                            }}
                          />
                          <label htmlFor="icon-button-file">
                            <IconButton
                              color="primary"
                              aria-label="upload picture"
                              component="span"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              size="small"
                              className={classes.modifyLogoButton}
                            >
                              <Create />
                            </IconButton>
                          </label>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Divider sx={{ margin: "0.5rem 0 1rem 0" }} />
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Typography variant="body1">
                      {t("myExpariences.class.classMembers")}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      sx={{ maxWidth: "50%" }}
                      startIcon={<Add />}
                      onClick={handleAddMember}
                    >
                      {t("myExpariences.class.addMemberButton")}
                    </Button>
                  </Box>
                  <Card elevation={8} className={classes.membersContainer}>
                    {classState.members.map((member, index) => (
                      <Card
                        elevation={10}
                        key={`member-number-${index}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          padding: "0.5rem",
                          marginBottom: "0.5rem",
                          position: "relative",
                        }}
                      >
                        <IconButton
                          color="primary"
                          onClick={() => setConfirmDelete({ index, member })}
                          sx={{
                            width: "40px",
                            height: "40px",
                            position: "absolute",
                            right: "0",
                            top: "35%",
                          }}
                        >
                          <Clear />
                        </IconButton>
                        <Box
                          sx={{
                            display: "flex",
                            margin: "0.5rem 0",
                          }}
                        >
                          <TextField
                            label={t("myExpariences.class.memberFirstName")}
                            value={member.firstname}
                            onChange={(e) =>
                              handleMemberChange(
                                index,
                                "firstname",
                                e.target.value
                              )
                            }
                            fullWidth
                            sx={{ marginRight: "1rem" }}
                          />
                          <TextField
                            label={t("myExpariences.class.memberLastName")}
                            value={member.lastname}
                            onChange={(e) =>
                              handleMemberChange(
                                index,
                                "lastname",
                                e.target.value
                              )
                            }
                            fullWidth
                            sx={{ marginRight: "2rem" }}
                          />
                        </Box>
                        <TextField
                          label={t("myExpariences.class.memberEmail")}
                          value={member.email}
                          required
                          error={
                            member.email.includes("@") &&
                            member.email.length > 0 &&
                            !isEmailValid(member.email)
                          }
                          onChange={(e) =>
                            handleMemberChange(index, "email", e.target.value)
                          }
                          sx={{ margin: "0.5rem 2rem 1rem 0" }}
                        />
                      </Card>
                    ))}
                  </Card>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={createOrUpdateClass}
                    disabled={
                      creatingOrUpdating.file ||
                      creatingOrUpdating.group ||
                      isAvailableForCreation()
                    }
                    sx={{
                      marginTop: "1rem",
                    }}
                  >
                    {creatingOrUpdating.file || creatingOrUpdating.group ? (
                      <CircularProgress size={28} />
                    ) : selectedClass?.id ? (
                      t("myExpariences.class.updateButton")
                    ) : (
                      t("myExpariences.class.createButton")
                    )}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Root>
        </Dialog>
      </>
    );
};

export default ClassCreator;
